import qs from 'qs'
import request from 'api/requestUtils'
import type { actions } from '@luxuryescapes/contract-svc-tag'

type GetManyTagsResponse = actions['getManyTags']['responses']['200']['content']['application/json']

export interface GetTagsByDestinationIdParams {
  destinationId: string
  region: string
  limit?: number
  offset?: number
  travelStartDate?: string
  travelEndDate?: string
  travelDateOverlap?: boolean
}

export interface GetTagsByOfferIdParams {
  offerId: string
  region: string
  limit?: number
  offset?: number
  travelStartDate?: string
  travelEndDate?: string
  travelDateOverlap?: boolean
}

export interface Tag {
  id: string
  name: string
  createdAt: string
  updatedAt: string
}

export interface GetTagsResult {
  tags: Array<Tag>
  pagination: {
    limit: number
    offset: number
    total_items: number
    has_next: boolean
    has_previous: boolean
  }
}

/**
 * Retrieves tags assigned to a destination by destination ID
 * @param params - Parameters including destinationId and optional pagination
 * @returns Promise with tags (including names) and pagination info
 */
export function getTagsByDestinationId(
  params: GetTagsByDestinationIdParams,
): Promise<GetTagsResult> {
  const { destinationId, region, limit, offset, travelStartDate, travelEndDate, travelDateOverlap } = params

  const query = qs.stringify({
    entityIdIn: [destinationId],
    entityTypeIn: ['DESTINATION'],
    region,
    ...(limit !== undefined && { limit }),
    ...(offset !== undefined && { offset }),
    ...(travelStartDate !== undefined && { travelStartDate }),
    ...(travelEndDate !== undefined && { travelEndDate }),
    ...(travelDateOverlap !== undefined && { travelDateOverlap }),
  }, {
    arrayFormat: 'brackets',
  })

  return request
    .get<GetManyTagsResponse>(`/api/tags/v1/tags?${query}`)
    .then((response) => ({
      tags: response.result,
      pagination: response.pagination,
    }))
}

/**
 * Retrieves tags assigned to an offer by offer ID
 * @param params - Parameters including offerId and optional pagination
 * @returns Promise with tags (including names) and pagination info
 */
export function getTagsByOfferId(
  params: GetTagsByOfferIdParams,
): Promise<GetTagsResult> {
  const { offerId, region, limit, offset, travelStartDate, travelEndDate, travelDateOverlap } = params

  const query = qs.stringify({
    entityIdIn: [offerId],
    region,
    ...(limit !== undefined && { limit }),
    ...(offset !== undefined && { offset }),
    ...(travelStartDate !== undefined && { travelStartDate }),
    ...(travelEndDate !== undefined && { travelEndDate }),
    ...(travelDateOverlap !== undefined && { travelDateOverlap }),
  }, {
    arrayFormat: 'brackets',
  })

  return request
    .get<GetManyTagsResponse>(`/api/tags/v1/tags?${query}`)
    .then((response) => ({
      tags: response.result,
      pagination: response.pagination,
    }))
}
